<script lang="ts" setup>
import ArrowLogout from 'assets/icons/exit.svg'
import PhoneIcon from '~/assets/icons/contacts/phone.svg?component';
import MailIcon from '~/assets/icons/contacts/mail.svg?component';
import ProfileIcon from '~/assets/icons/menuHeader/profile.svg?component';
import OrdersIcon from '~/assets/icons/menuHeader/orders.svg?component';
import BalanceIcon from '~/assets/icons/menuHeader/balance.svg?component';
import CartIcon from '~/assets/icons/menuHeader/cart.svg?component';
import LogoHeaderIcon from '~/assets/icons/logo-header.svg?component';
import LocationIcon from '~/assets/icons/location.svg?component';
import MenuBtnWhiteIcon from '~/assets/icons/burger_white_24x24.svg?component';
import MenuBtnBlackIcon from '~/assets/icons/burger_black_24x24.svg';
import LogoSmallIcon from '~/assets/icons/logo-small.svg?component';
import CloseIcon from '~/assets/icons/close/closeMobileModal.svg?component';
import CloseWhiteIcon from '~/assets/icons/close/24/close-white.svg?component';
import DiscountIconDesktop from '~/assets/icons/discount/16/discount-outlined.svg';
import DiscountIconMobile from '~/assets/icons/discount/24/discount-outlined.svg';
import CatalogMenu from "~/widgets/catalog/CatalogMenu.vue";
import AuthModal from "~/widgets/auth/AuthModal.vue";
import logout from "~/features/auth/logout";

import NavigationMenu from '~/entities/navigation/NavigationMenu.vue';
import BalancePopup from "~/entities/navigation/popups/BalancePopup.vue";
import ProfilePopup from "~/entities/navigation/popups/ProfilePopup.vue";
import SearchBar from "~/entities/searchBar/SearchBar.vue";
import SearchMobile from "~/entities/searchBar/SearchMobile.vue";

import {useWindowScroll} from '~/composables/scroll-event';
import {useMonitorSize} from "~/composables/monitor-size";
import useCartStore from '~/shared/store/modules/cart';
import useAccountStore from '~/shared/store/modules/account'
import useMainStore from '~/shared/store';
import OneSError from "~/entities/oneSErrors/OneSError.vue";
import OneSErrorModal from "~/entities/oneSErrors/OneSErrorModal.vue";
import useCompaniesStore from "~/shared/store/modules/account/companies";
import {useConfigStore} from "~/shared/store/modules/config";
import customFetch from '~/composables/custom-fetch';
import apiConfig from '~/shared/config/api';
import Modal from '~/components/ui/modals/Modal.vue';
import Spinner from '~/components/ui/preloader/Spinner.vue';
import AppButton from "~/components/ui/buttons/AppButton.vue";
import MenuDrop from "~/entities/navigation/MenuDrop.vue";
import config from "~~/shared/config"
import type {ICity} from '~/shared/interfaces/main';
import type {INavigationItemHeader} from '~/shared/interfaces/ui/navigation';
import type {IMonitorSize, IScrollInfo} from "~/shared/interfaces/composables";

const configStore = useConfigStore()
const mainStore = useMainStore();
const cartStore = useCartStore();
const accountStore = useAccountStore();
const companiesStore = useCompaniesStore()
const route = useRoute()
let sizes: IMonitorSize | null = useMonitorSize()
const catalogMenuBtnIcon = computed(() => isCatalogList.value ? CloseWhiteIcon : MenuBtnWhiteIcon)

let scrollInfo: IScrollInfo | null = null;
if (process.client) {
  scrollInfo = useWindowScroll();
}
const userInfo = accountStore.userProfile?.user;
const availableRoutes = ['account', 'cart', 'order']
const isOneSErrorModal = computed(() => {
  const routeName = route.fullPath.split('/').filter(el => el !== '')[0]
  return availableRoutes.includes(routeName)
})

const headerNavigation = computed(() => [
  {
    name: 'Бренды',
    href: '/brands',
  },
  {
    name: 'Поиск по VIN',
    href: '/vin',
    drop: [ {name: 'Поиск по VIN', href: '/vin'} , ...(mainStore?.vinBrands ? mainStore.vinBrands.map( i => { return {name: i.title, href: i.link} }) : []) ],
  },
  {
    name: 'О нас',
    drop: [
      {name: 'О компании', href: '/about'},
      {name: 'Бренд SOTRANS', href: '/about-brand'}
    ],
  },
  {
    name: 'Доставка',
    href: '/delivery',
  },
  {
    name: 'Акции',
    href: '/promotions',
    icon: DiscountIconDesktop,
    iconMobile: DiscountIconMobile
  },
  {
    name: 'Новости',
    href: '/news',
  },
  {
    name: 'Контакты',
    href: `/contacts/spb`,
  },
])

const { defaultCity } = config();

let preloaderEllipse = ref(false)
let locationList = ref(false)
let isCatalogList = ref(false)
let isNavList = ref(false)
let mobileSearchWindow = ref(false)

const headerContacts = [
  {
    name: 'phone',
    href: 'tel:88002000233',
    text: '8 (800) 200-02-33',
    icon: PhoneIcon,
  },
  {
    name: 'email',
    href: 'mailto:support@sotrans.ru',
    text: 'support@sotrans.ru',
    icon: MailIcon,
  }
]

const getMenuHeader = computed((): INavigationItemHeader[] => {
  const cartIndicatorSettings: INavigationItemHeader['indicator'] = {
    active: Boolean(cartStore.cartPreview.length),
    text: cartStore.cartPreview.length,
    color: 'orange',
  }

  const navigation: { [key: string]: INavigationItemHeader[] } = {
    auth: [
      {
        name: 'Профиль',
        href: '/account',
        icon: ProfileIcon,
        popup: 'profile',
        nameLink: 'account-nav-link',
      },
      {
        name: 'Заказы',
        href: '/account/orders',
        icon: OrdersIcon,
        nameLink: 'orders-nav-link',
      },
      {
        name: 'Баланс',
        href: companiesStore.isUserHasCompanies && accountStore.balance.length ? '#' : '/account/finance',
        icon: BalanceIcon,
        popup: companiesStore.isUserHasCompanies && accountStore.balance.length ? 'balance' : null,
        hideIcon: !companiesStore.isUserHasCompanies,
        nameLink: 'balance-nav-link',
        show: Number(configStore?.config?.showBalance)
      },
      {
        name: 'Корзина',
        href: '/cart',
        icon: CartIcon,
        indicator: cartIndicatorSettings,
        nameLink: 'cart-nav-link',
      }
    ],
    guest: [
      {
        name: 'Войти',
        href: 'javascript:',
        icon: ProfileIcon,
        clickFunc: () => mainStore.setAuthModalState(true),
        nameLink: 'login-nav-link'
      },
      {
        name: 'Корзина',
        href: '/cart',
        icon: CartIcon,
        indicator: cartIndicatorSettings,
        nameLink: 'cart-nav-link'

      }
    ]
  }

  if (accountStore.userRole != 'guest') {
    return navigation.auth;
  }

  return navigation.guest;
})

const messageErrorOneS = computed(() => useCookie('closeError').value ? '' : 'Уважаемые партнёры! На сайте ведутся технические работы. Персональные скидки и наличие товара могут отображаться некорректно. Оформляйте заказы через сайт, окончательная проверка осуществляется менеджером. Условия заказа будут с Вами согласованы. Приносим извинения за доставленные неудобства.')
watch(()=> accountStore.userProfile, ()=> {
  if(accountStore.userProfile && !useCookie('sotransCity').value) {
    getCity(accountStore.userProfile.user.city)
    useCookie('sotransCity').value = JSON.stringify(accountStore.userProfile.user.city)
  } else {
    let citySelected = useCookie('sotransCity').value || mainStore.sotransCities[0] || defaultCity
    getCity(citySelected)
  }
}, {immediate: true})

function getCity(city: ICity) {
  mainStore.setSotransCitySelected(city)
}

function toggleNavList(state: boolean) {
  mainStore.setBodyFixed(state)
  isNavList.value = state
}

async function updateCity(city: ICity) {
  mainStore.setContentLoadingStatus(false)
  preloaderEllipse.value = true

  if(useCookie('token').value) {
    const { account } = apiConfig;
    const { success } = await customFetch(account.updateStockCity({
      citySlug: city.slug,
    }));
  }
  mainStore.setSotransCitySelected(JSON.parse(JSON.stringify(city)))
  useCookie('sotransCity').value = JSON.stringify(city)
  locationList.value = !locationList.value
  preloaderEllipse.value = false
  mainStore.setContentLoadingStatus(true)
}

function openMobileSearch(open: boolean) {
  mobileSearchWindow.value = open
  mainStore.setBodyFixed(open)
}

function closeMenu () {
  toggleNavList(false)
}
</script>

<template>
  <header
    class="header"
    :class="scrollInfo?.isScrollDown && 'sticky'"
    :style="{ borderBottom: isCatalogList ? 'none' : '' }"
  >
    <div class="header__content container">
      <div class="header__logo-small">
        <NuxtLink to="/">
          <LogoSmallIcon/>
        </NuxtLink>
      </div>
      <div
        class="header__city svg-fill-orange"
        :id="mainStore.sotransCitySelected.slug"
        v-if="mainStore.sotransCitySelected"
        @click="
          mainStore.sotransCities.length ? (locationList = !locationList) : null
        "
      >
        <LocationIcon />
        <span>
          {{ mainStore.sotransCitySelected.name }}
        </span>
      </div>
      <div class="header__navigation">
        <div
          class="header__navigation-btn"
          @click="toggleNavList(true)"
        >
          <MenuBtnBlackIcon/>
        </div>

        <div
          class="header__navigation-menu"
          :class="isNavList ? 'active' : ''"
        >
          <div
            class="header__navigation-menu__close"
            @click="toggleNavList(false)"
          >
            <CloseIcon/>
          </div>
          <ClientOnly>
            <nav>
              <ul>
                <li
                  v-for="itemNavigation in headerNavigation"
                  :key="`${itemNavigation.name}-header-link`"
                >
                  <NuxtLink
                    v-if="!itemNavigation.drop"
                    :to="itemNavigation.href"
                    class="svg-fill-orange"
                    @click="closeMenu"
                  >
                    {{ itemNavigation.name }}
                    <component
                      v-if="itemNavigation.icon || itemNavigation.iconMobile"
                      :is="sizes?.isMobile ? itemNavigation.iconMobile : itemNavigation.icon"
                    />
                  </NuxtLink>
                  <MenuDrop
                    v-else
                    :drop-links="itemNavigation.drop"
                    :drop-name="itemNavigation.name"
                    :drop-href="itemNavigation.href"
                    :is-mobile="sizes?.isMobile"
                    @close-menu="closeMenu"
                  />
                </li>
              </ul>
            </nav>
          </ClientOnly>
          <div class="header__contacts">
            <a
              class="svg-fill-orange"
              v-for="(contact, i) in headerContacts"
              :href="contact.href"
            >
              <span>{{ contact.text }}</span>
            </a>
          </div>
        </div>
      </div>
    </div>
    <div class="header__content header__content-search container">
      <div class="header__logo">
        <NuxtLink to="/">
          <LogoHeaderIcon/>
        </NuxtLink>
      </div>
      <div class="header__catalog">
        <AppButton
          :icon="catalogMenuBtnIcon"
          text="Каталог"
          @click="isCatalogList = !isCatalogList"
        />
      </div>
      <SearchBar
        @openMobileSearch="openMobileSearch"
        :userInfo="userInfo"
      />
      <SearchMobile
        @openMobileSearch="openMobileSearch"
        v-show="mobileSearchWindow"
        :is-open="mobileSearchWindow"
      />
      <ClientOnly>
        <NavigationMenu
          class="header__menu"
          :navigation-menu="getMenuHeader"
        >
          <template #profile-popup>
            <ProfilePopup>
              <template #logout>
                <button
                  class="header__logout"
                  @click="logout"
                >
                  <ArrowLogout/>
                  Выйти
                </button>
              </template>
            </ProfilePopup>
          </template>
          <template #balance-popup>
            <BalancePopup v-if="accountStore.balance.length"/>
          </template>
        </NavigationMenu>
      </ClientOnly>
    </div>
    <CatalogMenu
      :is-active="isCatalogList"
      @close="isCatalogList = false"
    />
<!--    TODO: уведомления о технических работах на сайте -->
<!--    <ClientOnly>-->
<!--      <noindex>-->
<!--        <OneSError-->
<!--          :txt="messageErrorOneS"-->
<!--          :is-one-s-died="true"-->
<!--          v-if="!isOneSErrorModal"-->
<!--        />-->
<!--      </noindex>-->
<!--    </ClientOnly>-->
    <Teleport
      to="body"
      v-if="route.name !== 'order'"
    >
      <AuthModal/>
    </Teleport>
  </header>
  <ClientOnly>
      <Teleport to="body">
      <!--    TODO: уведомления о технических работах на сайте -->
      <!--      <noindex>-->
      <!--          <OneSErrorModal-->
      <!--            v-if="isOneSErrorModal"-->
      <!--          />-->
      <!--      </noindex>-->
      <Modal
        class="city-modal"
        modal-title="Выберите город"
        :modal-is-open="locationList"
        :full-height-on-mobile="true"
        @close-modal="locationList = !locationList"
      >
        <div class="cities-list">
          <ul>
            <li
              v-for="city in mainStore.sotransCities"
              :key="city.slug"
              @click="updateCity(city)"
              :class="
                city.name === mainStore.sotransCitySelected?.name && 'active'
              "
            >
              {{ city.name }}
            </li>
          </ul>
        </div>
      </Modal>
      <Spinner
        class="header__city-preloader"
        v-if="preloaderEllipse"
      />
    </Teleport>
  </ClientOnly>
</template>

<style lang="scss" scoped>
.modal.city-modal {
  :deep(.modal-content) {
    transition: none;

    @include sm-breakpoint {
      padding-top: 24px;
    }
  }

  :deep(.modal-close__wrapper) {
    @include sm-breakpoint {
      width: 24px !important;
      height: 24px !important;
      top: 24px !important;
      right: 20px !important;
    }
  }
}

.header {
  position: fixed;
  background: $bg_page;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  transform: translateY(0);
  transition: transform 0.3s;
  border-bottom: 1px solid $bg_border;

  .oneSError {
    position: absolute;
    top: calc(100% + 1px);
  }

  a {
    color: $content_primary;
  }

  &.sticky {
    transform: translateY(-48px);

    @include md-breakpoint {
      transform: translateY(-54px);
    }
  }

  &__logout {
    @include font(14px, 13px, 500);
    display: flex;
    align-items: center;
    gap: 4px;

    &:hover:not(:active) {
      @include mf-breakpoint {
        color: $control-content_negative;

        :deep(svg) {
          path {
            fill: $control-content_negative;
          }
        }
      }
    }
  }

  &__content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 48px;

    &:first-child {
      gap: 46px;

      @include md-breakpoint {
        gap: 0;
      }
    }

    &:nth-child(2) {
      height: 79px;

      @include md-breakpoint {
        height: 67px;
        padding-bottom: 20px;
      }
    }

    @include md-breakpoint {
      height: 64px;
      padding-top: 16px;
      padding-bottom: 16px;
    }
  }

  &__city {
    max-width: 148px;
    width: 100%;
    cursor: pointer;
    height: 20px;
    display: flex;
    align-items: flex-start;

    &:hover {
      color: $content_orange;
    }

    svg {
      margin-top: 1px;
    }

    span {
      @include font(14px, 20px, 500);
      padding-left: 4px;
      white-space: nowrap;
      transition: 0.3s ease;
    }

    &-preloader {
      height: 100vh;
      z-index: 1000;
      position: fixed;
    }
  }

  &__navigation {
    width: 100%;
    display: flex;
    align-items: center;

    @include md-breakpoint {
      justify-content: flex-end;
    }

    &-menu {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;

      @include md-breakpoint {
        height: calc(var(--vh, 1vh) * 100);
        position: absolute;
        left: 0;
        top: 0;
        flex-direction: column;
        align-items: flex-start;
        background: $bg_page;
        z-index: 1000;
        width: 100%;
        opacity: 0;
        pointer-events: none;

        &.active {
          opacity: 1;
          pointer-events: auto;
          overflow: auto;
        }
      }

      &__close {
        display: none;

        @include md-breakpoint {
          position: absolute;
          z-index: 11;
          top: 16px;
          right: 22px;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }
    }

    nav {
      @include md-breakpoint {
        padding: 64px 20px 20px;
        max-width: none;
        width: 100%;
      }

      ul {
        display: flex;
        align-items: center;
        gap: 16px;

        @include md-breakpoint {
          width: 100%;
          flex-direction: column;
          align-items: flex-start;
          gap: 40px;
        }

        li {
          @include md-breakpoint {
            width: 100%;
          }

          a {
            @include font(14px, 20px);
            display: flex;
            gap: 2px;
            align-items: center;

            @include md-breakpoint {
              @include font(20px, 24px, 600);
            }
          }
        }

        a {
          transition: 0.3s ease;

          &:hover {
            color: $content_orange;
          }

          @include md-breakpoint {
            font-weight: 600;
          }
        }
      }
    }

    &-btn {
      display: none;
      align-items: center;

      @include md-breakpoint {
        display: flex;
      }
    }
  }

  &__contacts {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;

    @include md-breakpoint {
      flex-direction: column;
      align-items: flex-start;
      background: $bg_light-grey;
      width: 100%;
      padding: 40px 20px;
      max-width: 100%;
      gap: 28px;
      border-radius: 20px 20px 0 0;
    }

    a {
      display: flex;
      align-items: center;
      gap: 4px;

      @include md-breakpoint {
        gap: 8px;
      }

      &:hover:not(:active) {
        @include mf-breakpoint {
          color: $content_orange;
        }
      }

      span {
        @include font(14px, 20px, 500);

        @include md-breakpoint {
          @include font(16px, 20px, 500);
        }
      }
    }
  }

  &__logo {
    margin-right: 16px;
    height: 48px;

    @include md-breakpoint {
      display: none;
    }

    &-small {
      display: none;
      padding-right: 8px;

      @include md-breakpoint {
        display: block;
      }
    }
  }

  &__catalog {
    position: relative;

    button {
      width: 128px;
      height: 48px;
      padding: 14px 16px 14px 16px;
      border-radius: 8px;
      display: flex;
      gap: 8px;
      align-items: center;
      justify-content: center;

      span {
        color: $content_white;
        @include font(16px, 20px, 500);
      }
    }

    ul {
      position: absolute;
      background: rgb(255, 255, 255);
      width: 100%;
      max-width: 128px;
      box-shadow: rgb(160, 160, 160) 5px 6px 10px 1px;
      top: calc(100% + 5px);
      border-radius: 8px;

      li {
        padding: 5px 10px;
      }
    }

    @include md-breakpoint {
      display: none;
    }
  }

  &__search {
    @include sm-breakpoint {
      margin: 0;
    }
  }

  &__menu {
    a:not(:last-child) {
      @include lg-breakpoint {
        margin-right: 10px;
      }
    }

    @include md-breakpoint {
      display: none;
    }
  }
}

.nav {
  ul {
    display: flex;
    justify-content: space-between;
  }
}

.cities-list {
  overflow: hidden;

  ul {
    display: flex;
    flex-wrap: wrap;
    padding-top: 24px;
    max-width: 572px;
    width: 100%;

    @include sm-breakpoint {
      display: block;
      overflow-y: scroll;
    }

    li {
      width: 50%;
      height: 48px;
      display: flex;
      align-items: center;
      transition: 0.3s;
      cursor: pointer;

      @include sm-breakpoint {
        width: 100%;
      }

      &.active {
        font-weight: 500;
        color: $control-content_orange;
      }

      &:hover:not(:active) {
        @include mf-breakpoint {
          color: $control-content_orange;
        }
      }
    }
  }
}
</style>
